import { useCallback, useEffect, useRef, useState } from 'react'
import { readDraft, writeDraft, clearDraft } from '../lib/draftStorage'
import { registerFlush } from './draftFlushRegistry'

/**
 * useDraft — persistence + restore for a single form's unsaved state.
 * PRIMARY mechanism is debounced write-through: every time `currentState`
 * changes, we schedule a localStorage write ~300ms later.  If a chunk
 * error hits, the latest committed state is already persisted (or
 * within a 300ms window, in which case the boundary's flushAllDrafts()
 * catches the ref-backed snapshot).
 *
 * Design after T2-B.next.B panel review (unanimous 4/5): the original
 * "flush-only-at-crash-time" design fought React 18 unmount ordering
 * (componentDidCatch fires AFTER the failed subtree unmounts + effects
 * cleaned up).  Debounced write-through as primary + registry as backup
 * eliminates that class of bug entirely — drafts survive unmount because
 * the last write is already in localStorage.
 *
 * @param saveKey — unique localStorage key (e.g. `invoice-${id}-mark-paid`).
 *                  Falsy = disable persistence entirely (early bail).  Namespace
 *                  by entity id + form to avoid collisions across pages.
 * @param currentState — the current form state, any JSON-serializable value.
 *                       Passing null/undefined disables persistence for that
 *                       render.  Pass fresh objects each render (React state
 *                       already does this) so effect deps detect changes.
 * @param opts.isDirty — optional predicate (state) => boolean.  Return false
 *                       to skip persistence for pristine forms.  Prevents
 *                       polluting localStorage with "no user input yet"
 *                       default state (Sol pre-code finding).
 * @param opts.debounceMs — default 300.  Higher for very chatty forms.
 *
 * @returns {
 *   draft: { state, savedAt, buildIdMismatch } | null — draft found on mount
 *                     (via lazy useState init — reads localStorage ONCE at
 *                     mount, doesn't re-fire on re-render).
 *   restore: () => draftState | null — reads draft, clears storage, returns
 *                     the raw state so caller can setState it into the form.
 *   discard: () => void — clears the stored draft without applying.
 *   flush: () => void — immediate sync write of current state (no debounce).
 *                     Callers should invoke on submit/close to sync state
 *                     BEFORE cleanup, so registered final-flush doesn't
 *                     re-write stale state after form was cleared.
 * }
 */
export default function useDraft (saveKey, currentState, opts = {}) {
  const debounceMs = opts.debounceMs ?? 300

  // Build the RESTORED state came from (draftStorage's originBuildId).  Null
  // until the user actually restores; from then on every write this hook makes
  // re-stamps that origin, so the red "app was updated" banner survives the
  // restore instead of quietly going amber on a second crash.
  //
  // Declared here rather than beside suppressFlushUntilRef because the saveKey
  // effect below also reads it, and a ref referenced above its own useRef is a
  // render-time ReferenceError that `node --check` does not catch.
  const originBuildIdRef = useRef(null)

  // Read draft when saveKey resolves.  Must re-read when saveKey changes
  // (from null → id on async entity load, or from id → new id when the
  // caller wants to persist a different entity's form).  Lazy useState
  // init handles the mount case; useEffect handles subsequent changes.
  const [draft, setDraft] = useState(() => (saveKey ? readDraft(saveKey) : null))
  useEffect(() => {
    // Switching entities ends the previous draft's lineage — without this,
    // restoring a cross-build draft on row 1 and then editing row 2 would
    // stamp row 2's draft with row 1's origin and show a banner it never
    // earned.
    originBuildIdRef.current = null
    setDraft(saveKey ? readDraft(saveKey) : null)
  }, [saveKey])

  // Refs keep latest state + isDirty predicate available for flush
  // callbacks that live longer than the current render — prevents
  // stale-closure snapshots when the boundary flushes at crash time.
  const stateRef = useRef(currentState)
  stateRef.current = currentState
  const isDirtyRef = useRef(opts.isDirty)
  isDirtyRef.current = opts.isDirty

  const timeoutRef = useRef(null)

  // Post-code Explore-agent finding: without this ref, `discard()` clears
  // localStorage but the subsequent unmount (from submit-success → close
  // chain) triggers useEffect's cleanup which calls the registered
  // snapshotFn → writes state to localStorage AGAIN → user sees stale
  // "Restored" banner on next open of the same form.  Suppression window
  // of 500ms covers React 18's async unmount timing (which resolves in a
  // single event-loop tick) but is short enough that a user who continues
  // typing after a genuine discard isn't blocked from re-persisting (post-
  // code panel: 2s was too generous — would suppress typing 1-2s after
  // discard).
  const suppressFlushUntilRef = useRef(0)

  // ── Primary: debounced write-through on state change ─────────
  // Runs on every render where currentState changed by reference.
  // The stateRef.current inside the setTimeout closure captures the
  // MOST RECENT value at fire time, so rapid successive changes only
  // produce one write per debounce window.
  useEffect(() => {
    if (!saveKey || currentState == null) return
    const isDirty = isDirtyRef.current
    if (typeof isDirty === 'function' && !isDirty(currentState)) return

    if (timeoutRef.current) clearTimeout(timeoutRef.current)
    timeoutRef.current = setTimeout(() => {
      // Same suppression window as the registry flush — if user just
      // discarded/restored, don't re-persist for 2s.
      if (Date.now() < suppressFlushUntilRef.current) return
      const s = stateRef.current
      const d = isDirtyRef.current
      if (typeof d === 'function' && !d(s)) return
      if (s == null) return
      writeDraft(saveKey, s, { originBuildId: originBuildIdRef.current || undefined })
    }, debounceMs)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentState, saveKey, debounceMs])

  // ── Secondary: register with flush registry (crash-time backup) ─
  // Belt-and-suspenders for the corner case where user typed a
  // keystroke less than `debounceMs` ago and a chunk error hits
  // BEFORE the pending setTimeout fires.  Registry snapshots via
  // ref-backed fn so we always get the latest committed state.
  //
  // Also: on natural unmount (cleanup returned by registerFlush),
  // the registry does a FINAL synchronous write — preserves state
  // when a modal closes normally without user submit/discard.
  useEffect(() => {
    if (!saveKey) return
    const snapshot = () => {
      // Suppression window guards against the discard→unmount race —
      // see suppressFlushUntilRef doc.
      if (Date.now() < suppressFlushUntilRef.current) return null
      const s = stateRef.current
      const d = isDirtyRef.current
      if (typeof d === 'function' && !d(s)) return null
      if (s == null) return null
      return s
    }
    return registerFlush(saveKey, snapshot)
  }, [saveKey])

  const restore = useCallback(() => {
    if (!saveKey) return null
    const d = readDraft(saveKey)
    if (!d) { setDraft(null); return null }
    // Captured BEFORE clearDraft — after it the slot is empty and there is
    // nothing left to read the origin from.
    originBuildIdRef.current = d.originBuildId || d.buildId || null
    clearDraft(saveKey)
    setDraft(null) // hide banner after user restores
    // Suppress the debounce+registry writes for a brief window — the
    // caller is about to setState the restored value, which would
    // otherwise trigger our debounced write to re-persist a "draft"
    // that's now the live state.
    suppressFlushUntilRef.current = Date.now() + 500
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    return d.state
  }, [saveKey])

  const discard = useCallback(() => {
    if (saveKey) clearDraft(saveKey)
    setDraft(null) // hide banner after user discards
    // A discarded draft's lineage is over; a later unrelated edit under the
    // same saveKey must not inherit its red banner.
    originBuildIdRef.current = null
    // Suppress any pending debounced write AND the imminent registry
    // final-flush (fires on component unmount from submit-success →
    // close chain).  Without this, `discard()` clears localStorage but
    // the unmount immediately re-writes the state we just cleared.
    suppressFlushUntilRef.current = Date.now() + 500
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [saveKey])

  const flush = useCallback(() => {
    if (!saveKey) return
    const s = stateRef.current
    const d = isDirtyRef.current
    if (typeof d === 'function' && !d(s)) return
    if (s == null) return
    // Cancel any pending debounced write since we're doing sync now
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
    writeDraft(saveKey, s, { originBuildId: originBuildIdRef.current || undefined })
  }, [saveKey])

  return { draft, restore, discard, flush }
}
